import { StatusBadgeWithDot } from '@modules/Orders/List/components/display/StatusBadgeWithDot';
import {
  defaultLogisticStatusDot,
  getStatusForDropdown,
  logisticStatusColorMap,
  ORDER_STATUSES_ELIGIBLE_FOR_REFRESH_LOGISTICS,
} from '@modules/Orders/List/config/orderStatusConfig';
import { Button } from '@shared/components/ui/button';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { formatDateWithTime } from '@shared/utils/FormatUtils';
import { faClockRotateLeft, faTruck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShipmentStatusCorrections } from '../../hooks/useShipmentStatusCorrections';
import type { OrderShipment } from '../../models/Order';
import { ShipmentStatusCorrectionsModal } from '../modals/ShipmentStatusCorrectionsModal';

interface LogisticStatusProps {
  shipment?: OrderShipment;
  statusId?: number;
}

export const LogisticStatus: FC<LogisticStatusProps> = ({ shipment, statusId }) => {
  const { t } = useTranslation('viewOrdersModule');
  const correctionsModalRef = useRef<ModalCenterRef>(null);
  const { data: corrections, isLoading: correctionsLoading } = useShipmentStatusCorrections();

  const orderStatus = statusId ? getStatusForDropdown(statusId) : null;
  const isEligibleStatus = orderStatus !== null && ORDER_STATUSES_ELIGIBLE_FOR_REFRESH_LOGISTICS.includes(orderStatus);
  const hasLogisticData = Boolean(shipment?.status_mapped || shipment?.shipment_status);

  if (!isEligibleStatus && !hasLogisticData) {
    return null;
  }

  const hasCorrections = (corrections?.length ?? 0) > 0;
  const statusMapped = shipment?.status_mapped;
  const internalStatus = shipment?.shipment_status;

  const renderBadge = () => {
    if (statusMapped) {
      const { dot } = logisticStatusColorMap[statusMapped.id] ?? defaultLogisticStatusDot;
      return <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={statusMapped.name} />;
    }

    if (internalStatus) {
      return (
        <StatusBadgeWithDot
          className="text-xs"
          dotClassName={defaultLogisticStatusDot.dot}
          label={internalStatus.name}
        />
      );
    }

    return (
      <StatusBadgeWithDot
        className="text-xs"
        dotClassName={defaultLogisticStatusDot.dot}
        label={t('sections.logistic-status.empty')}
      />
    );
  };

  const handleOpenCorrections = () => {
    correctionsModalRef.current?.openModal();
  };

  return (
    <>
      <SectionBox className="bg-card-bg" data-testid="order-logistic-status">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">{t('sections.logistic-status.title')}</h2>
          {!correctionsLoading && hasCorrections && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenCorrections}
              className="bg-card-bg"
              aria-label={t('sections.logistic-status.view-corrections')}
              data-testid="order-logistic-status-corrections-button"
            >
              <FontAwesomeIcon icon={faClockRotateLeft} className="!w-3 !h-3" />
            </Button>
          )}
        </div>
        <div className="pt-6 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-hover-press">
            <FontAwesomeIcon icon={faTruck} className="!h-4 !w-4 text-icons" />
          </div>
          <div className="min-w-0">
            <div>{renderBadge()}</div>
            {shipment?.date_tracking_update && (
              <p className="mt-1 text-xs text-complementary-text">
                {t('sections.logistic-status.last-update')}: {formatDateWithTime(shipment.date_tracking_update)}
              </p>
            )}
          </div>
        </div>
      </SectionBox>
      <ShipmentStatusCorrectionsModal ref={correctionsModalRef} />
    </>
  );
};
